import {
  ActionBar,
  ActionType,
  AnchoredPanelComponent,
  BookmarkablePage,
  DetailView,
  DetailViewsPanel,
  ErrorViewPanel,
  FormValues,
  getResourceLifecycleStatus,
  getValue,
  GroupItem,
  InputFormGroup,
  LabelMetaItem,
  MetaItemSection,
  stateT,
  Status,
  TagsDisplay,
  uniqueGUID,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Link, Spinner, Stack } from "@fluentui/react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { OciVmDbSystemLink } from "../../components/OciLinks/OciVmDbSystemLink";
import { Fields as TagDetailsFields, TagDetails } from "../../components/TagDetails/TagDetails";
import { ConsoleContext } from "../../console/ConsoleContext";
import {
  DetailsPanelId,
  InlineFormId,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  MainTargetId,
  metaItemLabelsGap,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
} from "../../constants/uiConstants";
import { buildId } from "../../helpers/idHelper";
import { getStatusInfo, getTitleSuffix, optimizedRetryOption, ResourceStatus } from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { newVmDbSystemDelete, VmDbSystemDeleteProps } from "../../operations/VmDbSystem/VmDbSystemDelete";
import { newVmDbSystemEditTags, VmDbSystemEditTagsProps } from "../../operations/VmDbSystem/VmDbSystemEditTags";
import { newVmDbSystemUpdateTags, VmDbSystemUpdateTagsProps } from "../../operations/VmDbSystem/VmDbSystemUpdateTags";
import { getAzureLocationName, getOciRegion } from "../../utils";
import { VmDbSystemConfigurationDetails } from "./VmDbSystemConfigurationDetails";
import { VmDbSystemDatabasesList } from "./VmDbSystemDatabasesList";
import { VmDbSystemDbHomesList } from "./VmDbSystemDbHomesList";
import { VmDbSystemDbNodesList } from "./VmDbSystemDbNodesList";
import { VmDbSystemNetworkingDetails } from "./VmDbSystemNetworkingDetails";
import { VmDbSystemSecurityDetails } from "./VmDbSystemSecurityDetails";

export enum ActionIds {
  Refresh = "refresh",
  Delete = "delete",
  EditTags = "edit-tags",
}
export enum ActionBarTestIds {
  Refresh = "refresh",
  Delete = "delete",
}

export enum MetaItemSectionTestIds {
  RESOURCE_GROUP = "mis-resource-group",
  STATUS = "mis-status",
  LOCATION = "mis-loaction",
  SUBSCRIPTION = "mis-subscription",
  SUBSCRIPTION_ID = "mis-subscription-id",
  TAGS = "mis-tags",
  OCI_RESOURCE = "mis-oci-resource",
}

export enum MetaItemActionTestIds {
  EditTags = "edit-tags",
}

export enum LinkTestIds {
  AddTags = "add-tags-link",
}

const registrationIds = PageRegistrationConfig[PageId.VMDB_SYSTEM_DETAILS].map(config => config.key);

const getViewId = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.CONTAINERDATABASES:
      return registrationIds[7];
    case DetailsPanelId.DATABASEHOMES:
      return registrationIds[6];
    case DetailsPanelId.DATABASENODES:
      return registrationIds[5];
    case DetailsPanelId.SECURITY:
      return registrationIds[4];
    case DetailsPanelId.NETWORKING:
      return registrationIds[3];
    case DetailsPanelId.CONFIGURATION:
      return registrationIds[2];
    case DetailsPanelId.TAGS:
      return registrationIds[1];
    case DetailsPanelId.OVERVIEW:
    default:
      return registrationIds[0];
  }
};

export const VmDbSystemDetailsPage = (): JSX.Element => {
  const { subscriptionId, resourceGroup, provider, resourceType, resourceName, panelId } = useParams();
  const [searchParams] = useSearchParams();

  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");
  const location = searchParams.get("location") || "";
  const vmDbSystemId = buildId({
    subscriptionId: subscriptionId || "",
    resourceGroup: resGroup,
    provider: provider || "",
    resourceType: resourceType || "",
    resourceName: resName,
  });

  const { trackActionClick, trackActionDiscard, trackFormSave } = useAnalytics();

  const { back, navigateToPanel, navigateToSelf } = useNavigation(ConsoleContext);

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    const registrationConfig = PageRegistrationConfig[PageId.VMDB_SYSTEM_DETAILS].find(
      config => config.panelPath === panelId,
    );
    const urlQueryParams = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const path = `${RESOURCE_ROUTE}/${vmDbSystemId}/${panelId}${urlQueryParams}`;
    navigateToSelf(path, registrationConfig?.key || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tagTabKey, setTagTabKey] = React.useState<string>(uniqueGUID());

  const panelRef = React.useRef<AnchoredPanelComponent>();
  const setPanelRef = (panel: AnchoredPanelComponent): void => {
    panelRef.current = panel;
  };

  const onClose = (): void => back();

  const onMenuItemSelect = (id: string): void => navigateToPanel(id);

  const { subscriptions } = useSubscriptions();
  const subscription = subscriptions?.find(item => item.id === subscriptionId);

  const { response, loading, error, refresh } = useQueryCall({
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.getVmdbDbSystem,
    options: {
      args: {
        subscriptionId: subscriptionId || "",
        resourceGroupName: resGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        dbSystemName: resName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVmDbSystem(),
      },
      excludeErrorStatus: [404],
    },
  });

  const dbSystem = response?.data;
  const isLoading = loading && !dbSystem;
  const dbSystemName = dbSystem?.name || resName;

  const { trigger: triggerDeleteSystem } = useOperation<VmDbSystemDeleteProps>(newVmDbSystemDelete);
  const { trigger: triggerEditTagsSystem } = useOperation<VmDbSystemEditTagsProps>(newVmDbSystemEditTags);
  const { trigger: triggerUpdateTagsSystem } = useOperation<VmDbSystemUpdateTagsProps>(newVmDbSystemUpdateTags);

  const { closePanels } = useSidePanel();

  const overviewActionItems: ActionType[] = [
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW);
        refresh();
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      title: Messages.actions.delete(),
      description: Messages.hints.deleteVMDbSystemConfirmation(dbSystemName),
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW);
      },
      onConfirm: () => {
        triggerDeleteSystem({
          dbSystemId: dbSystem?.id,
          location,
          onExecute: () => {
            refresh();
            onClose();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW),
    },
  ];

  const onEditTags = (): void => {
    triggerEditTagsSystem({
      targetId: MainTargetId,
      dbSystemId: dbSystem?.id,
      defaultTags: tagsDefaultValue,
      location,
      onExecute: refresh,
      onCancel: () => trackActionDiscard(ActionIds.EditTags, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW),
    });
  };

  React.useEffect(() => {
    setTagTabKey(uniqueGUID);
  }, [dbSystem]);

  const tagsDefaultValue: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
  }[] = Object.keys(dbSystem?.freeformTags || {})
    .map((key: string) => ({ name: key, value: dbSystem?.freeformTags ? dbSystem?.freeformTags[key] : "" }));

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack tokens={{ childrenGap: metaItemLabelsGap }} key="vmdb-system-details-left-columm">
      <LabelMetaItem testId={MetaItemSectionTestIds.RESOURCE_GROUP} label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink resourceId={dbSystem?.id as string} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.STATUS} label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(dbSystem?.status || "")}
          status={getResourceLifecycleStatus(dbSystem?.status || "")}
          statusInfo={
            getStatusInfo(
              dbSystem?.status || "",
              dbSystem?.lastOperationStatus || "",
              dbSystem?.lastOperationStatusDetails || "",
            )
          }
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.LOCATION} label={Messages.labels.location()}>
        {getAzureLocationName(dbSystem?.location || "")}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION} label={Messages.labels.subscription()}>
        <AzureSubscriptionLink resourceId={dbSystem?.id as string} subscriptionName={subscription?.name} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION_ID} label={Messages.labels.subscriptionId()}>
        {subscriptionId || ""}
      </LabelMetaItem>
      <LabelMetaItem
        testId={MetaItemSectionTestIds.TAGS}
        label={Messages.labels.tags()}
        action={{
          id: ActionIds.EditTags,
          testId: MetaItemActionTestIds.EditTags,
          label: Messages.actions.edit(),
          onClick: () => {
            if (onEditTags) {
              trackActionClick(ActionIds.EditTags, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW);
              onEditTags();
            }
          },
        }}
      >
        {tagsDefaultValue?.length > 0
          ? (
            <TagsDisplay
              tags={tagsDefaultValue}
            />
          )
          : (
            <Link
              onClick={() => {
                trackActionClick(ActionIds.EditTags, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.OVERVIEW);
                onEditTags();
              }}
              data-test-id={LinkTestIds.AddTags}
            >
              {Messages.actions.addTags()}
            </Link>
          )}
      </LabelMetaItem>
    </Stack>,
    <Stack tokens={{ childrenGap: metaItemLabelsGap }} key="vmdb-system-details-right-columm">
      <LabelMetaItem testId={MetaItemSectionTestIds.OCI_RESOURCE} label={Messages.labels.ociResource()}>
        <OciVmDbSystemLink
          id={dbSystem?.id || ""}
          location={location}
          subscriptions={subscriptions || []}
          analytics={{ pageId: PageId.VMDB_SYSTEM_DETAILS, panelId: DetailsPanelId.OVERVIEW }}
        />
      </LabelMetaItem>
    </Stack>,
  ];

  const detailViews: DetailView[] = [
    {
      id: registrationIds[0],
      content: (
        <Stack style={{ height: "100%" }} tokens={{ childrenGap: 10 }}>
          <ActionBar actions={overviewActionItems} onActionClick={closePanels} />
          <MetaItemSection title={Messages.detailVmDbSystem.overview.sectionTitles.essentials()}>
            {essentialsMetaItemGroup}
          </MetaItemSection>
        </Stack>
      ),
    },
    {
      id: registrationIds[1],
      content: (
        <TagDetails
          key={tagTabKey}
          tagsDefaultValue={tagsDefaultValue}
          onApply={(formValues: FormValues) => {
            trackFormSave(InlineFormId.TAGS, PageId.VMDB_SYSTEM_DETAILS, DetailsPanelId.TAGS);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tags: { name: string; value: any; }[] = getValue(formValues, TagDetailsFields.Tags, InputFormGroup);
            triggerUpdateTagsSystem({
              dbSystemId: dbSystem?.id,
              tags,
              location,
            });
          }}
        />
      ),
    },
    {
      id: registrationIds[2],
      content: (
        <VmDbSystemConfigurationDetails
          dbSystem={dbSystem}
          location={location}
          refresh={refresh}
        />
      ),
    },
    {
      id: registrationIds[3],
      content: (
        <VmDbSystemNetworkingDetails
          dbSystem={dbSystem}
          location={location}
          subscriptionId={subscriptionId || ""}
          resourceGroupName={resGroup}
        />
      ),
    },
    {
      id: registrationIds[4],
      content: (
        <VmDbSystemSecurityDetails
          dbSystem={dbSystem}
          location={location}
          refresh={refresh}
        />
      ),
    },
    {
      id: registrationIds[5],
      content: (
        <VmDbSystemDbNodesList
          dbSystem={dbSystem}
          location={location}
          resGroup={resGroup}
          subscriptionId={subscriptionId || ""}
        />
      ),
    },
    {
      id: registrationIds[6],
      content: (
        <VmDbSystemDbHomesList
          dbSystem={dbSystem}
          location={location}
          resGroup={resGroup}
          subscriptionId={subscriptionId || ""}
        />
      ),
    },
    {
      id: registrationIds[7],
      content: (
        <VmDbSystemDatabasesList
          dbSystem={dbSystem}
          location={location}
          resourceGroupName={resGroup}
          subscriptionId={subscriptionId || ""}
        />
      ),
    },
  ];

  const groupItems: GroupItem[] = [
    {
      items: [
        {
          icon: SvgIconIds.dbSystemSvg,
          id: detailViews[0].id,
          name: Messages.labels.overview(),
        },
        {
          icon: SvgIconIds.tagsSvg,
          id: detailViews[1].id,
          name: Messages.labels.tags(),
        },
      ],
    },
    {
      heading: Messages.labels.settings(),
      items: [
        { icon: SvgIconIds.configurationSvg, id: detailViews[2].id, name: Messages.labels.configuration() },
        { icon: SvgIconIds.networkingSvg, id: detailViews[3].id, name: Messages.labels.networking() },
        { icon: SvgIconIds.securitySvg, id: detailViews[4].id, name: Messages.labels.security() },
      ],
    },
    {
      heading: Messages.labels.resources(),
      items: [
        { icon: SvgIconIds.databaseNodesSvg, id: detailViews[5].id, name: Messages.labels.databaseNodes() },
        { icon: SvgIconIds.databaseSvg, id: detailViews[6].id, name: Messages.labels.databaseHomes() },
        { icon: SvgIconIds.pluggableSvg, id: detailViews[7].id, name: Messages.labels.containerDatabases() },
      ],
    },
  ];

  const renderedContent = (): JSX.Element => {
    if (isLoading) {
      return <Spinner label={Messages.common.loading()} />;
    } if (error) {
      const isNotFound = error?.status === 404;
      const errorTitle = isNotFound
        ? Messages.detailVmDbSystem.loadError.notFound.title()
        : Messages.detailVmDbSystem.loadError.general.title();
      const errorDetail = isNotFound
        ? Messages.detailVmDbSystem.loadError.notFound.details()
        : Messages.detailVmDbSystem.loadError.general.details();

      return (
        <ErrorViewPanel
          anchorId="page"
          icon={SvgIconIds.dbSystemSvg}
          title={dbSystemName}
          errorTitle={errorTitle}
          resourceId={vmDbSystemId}
          errorCode={error?.status}
          details={[
            errorDetail,
            Messages.notifications.apiErrorMsg(error?.body?.message),
          ]}
          isOpen
          onClose={onClose}
        />
      );
    }
    return (
      <DetailViewsPanel
        anchorId="page"
        componentRef={setPanelRef}
        title={dbSystemName}
        subTitle={Messages.detailVmDbSystem.titles.long()}
        icon={SvgIconIds.dbSystemSvg}
        onClose={onClose}
        isOpen
        views={detailViews}
        activeViewId={getViewId(panelId)}
        onMenuItemSelect={onMenuItemSelect}
        menu={groupItems}
        status={dbSystem?.status !== ResourceStatus.Active ? {
          status: getResourceLifecycleStatus(dbSystem?.status || ""),
          tooltip: stateT(dbSystem?.status || ""),
        } : undefined}
      />
    );
  };

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={dbSystem?.name || Messages.detailVmDbSystem.titles.short()}
      titleSuffix={dbSystem?.name ? getTitleSuffix(panelId || "") : undefined}
    >
      {renderedContent()}
    </BookmarkablePage>
  );
};
